import React, { useState, useCallback } from 'react';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CFormInput, CFormSelect, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilPlus, cilSearch, cilPencil } from '@coreui/icons';
import CIcon from '@coreui/icons-react';

const urlApi = '/support-channels';
const urlRouter = '/support-channels';
const itemsPerPage = 30;

export async function LoaderSupportChannels() {
	return AxiosInstance.get(urlApi, { params: { page: 1, itemsPerPage } });
}

const SupportChannels = () => {
	const loaderData = useLoaderData();
	const navigate = useNavigate();
	const [rows, setRows] = useState(loaderData?.data?.data ?? []);
	const [rowCountState, setRowCountState] = useState(loaderData?.data?.total ?? 0);
	const [nameFilter, setNameFilter] = useState('');
	const [activeFilter, setActiveFilter] = useState('');
	const [showLoader, setShowLoader] = useState(false);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const getRowsTable = (p = 1) => {
		setShowLoader(true);
		AxiosInstance.get(urlApi, { params: { page: p, itemsPerPage } }).then((response) => {
			setRows(response.data.data ?? []);
			setRowCountState(response.data.total ?? 0);
			setShowLoader(false);
		}).catch(() => {
			setShowLoader(false);
		});
	};

	const handlePageChange = (page) => {
		getRowsTable(page + 1);
	};

	const filteredRows = rows.filter(r => {
		const matchesName = !nameFilter
			|| r.name?.toLowerCase().includes(nameFilter.toLowerCase())
			|| r.code?.toLowerCase().includes(nameFilter.toLowerCase());
		const matchesActive = activeFilter === ''
			? true
			: activeFilter === '1' ? r.is_active === true : r.is_active === false;
		return matchesName && matchesActive;
	});

	const columns = [
		{ field: 'id', headerName: 'ID', width: 70 },
		{
			field: 'name',
			headerName: 'Назва',
			width: 250,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className="table-name-link" title={params.row.name}>
					{params.row.name}
				</Link>
			),
		},
		{ field: 'code', headerName: 'Код', width: 150 },
		{ field: 'sort_order', headerName: 'Порядок', width: 75 },
		{
			field: 'is_active',
			headerName: 'Активна',
			width: 80,
			renderCell: (params) => (
				<CBadge color={params.row.is_active ? 'success' : 'secondary'}>
					{params.row.is_active ? 'Активна' : 'Неактивна'}
				</CBadge>
			),
		},
		{
			field: 'action',
			headerName: 'Переглянути',
			headerAlign: 'center',
			align: 'center',
			sortable: false,
			width: 105,
			renderCell: (params) => (
				<Link to={`${urlRouter}/view/${params.row.id}`} className='custom-view-icon' title="Переглянути запис">
					<CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
		{
			field: 'action_edit',
			headerName: 'Редагувати',
			headerAlign: 'center',
			align: 'center',
			sortable: false,
			width: 95,
			renderCell: (params) => (
				<Link to={`${urlRouter}/update/${params.row.id}`} className='custom-edit-icon' title="Редагувати запис">
					<CIcon icon={cilPencil} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
	];

	return (
		<CRow>
			<CCol xs={12}>
				<CCard className="mb-4">
					<CAccordion flush>
						<CAccordionItem>
							<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
							<CAccordionBody>
								<CRow className="my-3 mx-2">
									<CCol md={4} className="filter-field">
										<CFormInput
											placeholder="Назва або код"
											value={nameFilter}
											onChange={(e) => setNameFilter(e.target.value)}
											onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={3} className="filter-field">
										<CFormSelect
											value={activeFilter}
											onChange={(e) => setActiveFilter(e.target.value)}
											title="Фільтр по статусу активності"
										>
											<option value="">Всі (активні + неактивні)</option>
											<option value="1">Тільки активні</option>
											<option value="0">Тільки неактивні</option>
										</CFormSelect>
									</CCol>
									<CCol md={3} className="ms-auto" style={{ textAlign: 'end' }}>
										<CButton
											color="secondary"
											variant="outline"
											onClick={() => { setNameFilter(''); setActiveFilter(''); }}
											title="Скинути всі фільтри"
										>
											Очистити фільтри
										</CButton>
									</CCol>
								</CRow>
							</CAccordionBody>
						</CAccordionItem>
					</CAccordion>

					<CCardHeader>
						<strong>Канали зв'язку</strong>
					</CCardHeader>
					<CCardBody>
						<p className="text-medium-emphasis small">
							<Link to={`${urlRouter}/create`}>
								<CButton color="secondary" title="Створити новий канал зв'язку">
									<CIcon icon={cilPlus} customClassName="nav-icon" height={15} />
									Створити
								</CButton>
							</Link>
						</p>
						<div style={{ height: 600, width: '100%' }}>
							<DataGrid
								rows={filteredRows}
								columns={columns}
								pageSize={itemsPerPage}
								rowsPerPageOptions={[itemsPerPage]}
								pagination
								paginationMode="server"
								rowCount={rowCountState}
								loading={showLoader}
								onPageChange={handlePageChange}
								initialState={{
									sorting: { sortModel: [{ field: 'sort_order', sort: 'asc' }] },
								}}
							/>
						</div>
						<div className="d-flex justify-content-start mt-3">
							<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
								Повернутись
							</CButton>
						</div>
					</CCardBody>
				</CCard>
			</CCol>
		</CRow>
	);
};

export default SupportChannels;
